import { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Shield, Users, DollarSign, Dumbbell, TrendingUp, Activity, Crown, ChevronDown, ChevronUp, Mail, XCircle, Send, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const StatCard = ({ icon, label, value, subtext, color }) => (
  <Card className="bg-card border-border">
    <CardContent className="p-5">
      <div className="flex items-center gap-3 mb-2">
        <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${color}`}>
          {icon}
        </div>
        <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{label}</span>
      </div>
      <p className="text-2xl md:text-3xl font-black" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>{value}</p>
      {subtext && <p className="text-xs text-muted-foreground mt-1">{subtext}</p>}
    </CardContent>
  </Card>
);

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAllUsers, setShowAllUsers] = useState(false);
  const [sortField, setSortField] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [nudging, setNudging] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [grantingId, setGrantingId] = useState(null);
  const [sendingWeekly, setSendingWeekly] = useState(false);
  const [sendingWinback, setSendingWinback] = useState(false);
  const [syncingBurpees, setSyncingBurpees] = useState(false);

  const refreshUsers = async () => {
    const [statsRes, usersRes] = await Promise.all([
      axios.get('/api/admin/stats', { withCredentials: true }),
      axios.get('/api/admin/users', { withCredentials: true })
    ]);
    setStats(statsRes.data);
    setUsers(usersRes.data);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, usersRes, txRes] = await Promise.all([
          axios.get('/api/admin/stats', { withCredentials: true }),
          axios.get('/api/admin/users', { withCredentials: true }),
          axios.get('/api/admin/transactions', { withCredentials: true })
        ]);
        setStats(statsRes.data);
        setUsers(usersRes.data);
        setTransactions(txRes.data);
      } catch (error) {
        if (error.response?.status === 403) {
          toast.error('Admin access required');
        } else {
          toast.error('Failed to load admin data');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleNudge = async () => {
    setNudging(true);
    try {
      const res = await axios.post('/api/admin/nudge-unpaid', {}, { withCredentials: true });
      toast.success(`Nudge emails sent to ${res.data.sent} unpaid users!`);
      if (res.data.failed > 0) {
        toast.error(`${res.data.failed} failed: ${res.data.failed_emails?.join(', ') || 'unknown'}`);
      }
    } catch (error) {
      toast.error('Failed to send nudge emails');
    } finally {
      setNudging(false);
    }
  };

  const handleCancelUser = async () => {
    if (!cancelTarget) return;
    setCancelling(true);
    try {
      const res = await axios.post('/api/admin/cancel-user-subscription', { user_id: cancelTarget.user_id }, { withCredentials: true });
      toast.success(res.data.message);
      setCancelTarget(null);
      await refreshUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel subscription');
    } finally {
      setCancelling(false);
    }
  };

  const handleGrantVIP = async (user) => {
    if (!window.confirm(`Grant VIP access to ${user.name || user.email}? They'll instantly unlock all 155 workouts.`)) return;
    setGrantingId(user.user_id);
    try {
      const res = await axios.post('/api/admin/grant-vip', { user_id: user.user_id }, { withCredentials: true });
      toast.success(res.data.message);
      await refreshUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to grant VIP');
    } finally {
      setGrantingId(null);
    }
  };

  const handleSendWeekly = async () => {
    if (!window.confirm('Send weekly progress recap email to all eligible VIPs now?')) return;
    setSendingWeekly(true);
    try {
      const res = await axios.post('/api/admin/send-weekly-progress', {}, { withCredentials: true });
      toast.success(`Weekly recaps sent to ${res.data.sent} VIPs (${res.data.failed} failed)`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send weekly emails');
    } finally {
      setSendingWeekly(false);
    }
  };

  const handleSendWinback = async () => {
    if (!window.confirm('Send win-back email to all users inactive for 7+ days now?')) return;
    setSendingWinback(true);
    try {
      const res = await axios.post('/api/admin/send-winback', {}, { withCredentials: true });
      toast.success(`Win-back emails sent to ${res.data.sent} users (${res.data.failed} failed)`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send win-back emails');
    } finally {
      setSendingWinback(false);
    }
  };

  const handleSyncBurpees = async () => {
    if (!window.confirm('Sync Burpee demo photos to all workouts that have Burpees in the description?')) return;
    setSyncingBurpees(true);
    try {
      const res = await axios.post('/api/admin/sync-burpee-photos', {}, { withCredentials: true });
      toast.success(res.data.message);
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('Sync endpoint not deployed yet. Please redeploy the app and try again.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to sync Burpee photos');
      }
    } finally {
      setSyncingBurpees(false);
    }
  };

  const unpaidCount = users.filter(u => !u.is_vip).length;

  const sortedUsers = [...users].sort((a, b) => {
    let aVal = a[sortField] || '';
    let bVal = b[sortField] || '';
    if (sortField === 'workouts_completed') {
      aVal = Number(aVal) || 0;
      bVal = Number(bVal) || 0;
    }
    if (sortDir === 'asc') return aVal > bVal ? 1 : -1;
    return aVal < bVal ? 1 : -1;
  });

  const displayedUsers = showAllUsers ? sortedUsers : sortedUsers.slice(0, 20);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDir === 'asc' ? <ChevronUp className="w-3 h-3 inline" /> : <ChevronDown className="w-3 h-3 inline" />;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-muted-foreground">Loading admin dashboard...</div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-red-400">Access denied or failed to load data</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-4 md:p-8" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-2">
          <Shield className="w-7 h-7 text-red-500" />
          <div>
            <h1 className="text-3xl md:text-5xl font-bold tracking-tight uppercase">Admin</h1>
            <p className="text-base text-muted-foreground">Subscriber & revenue overview</p>
          </div>
        </div>

        {/* Stats Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4"
        >
          <StatCard
            icon={<Users className="w-5 h-5 text-blue-400" />}
            label="Total Users"
            value={stats.total_users}
            color="bg-blue-500/10"
          />
          <StatCard
            icon={<Crown className="w-5 h-5 text-yellow-400" />}
            label="VIP Subscribers"
            value={stats.vip_users}
            subtext={`${stats.total_users > 0 ? Math.round((stats.vip_users / stats.total_users) * 100) : 0}% conversion`}
            color="bg-yellow-500/10"
          />
          <StatCard
            icon={<DollarSign className="w-5 h-5 text-green-400" />}
            label="Total Revenue"
            value={`$${stats.total_revenue.toLocaleString()} AUD`}
            subtext={`${stats.paid_transactions} transactions`}
            color="bg-green-500/10"
          />
          <StatCard
            icon={<Dumbbell className="w-5 h-5 text-primary" />}
            label="Workouts Done"
            value={stats.total_workouts_completed}
            color="bg-primary/10"
          />
          <StatCard
            icon={<TrendingUp className="w-5 h-5 text-cyan-400" />}
            label="New This Week"
            value={stats.new_users_this_week}
            subtext="signups"
            color="bg-cyan-500/10"
          />
          <StatCard
            icon={<Activity className="w-5 h-5 text-purple-400" />}
            label="Active This Week"
            value={stats.active_users_this_week}
            subtext="completed a workout"
            color="bg-purple-500/10"
          />
        </motion.div>

        {/* Nudge Unpaid Users */}
        {unpaidCount > 0 && (
          <Card className="bg-card border-border">
            <CardContent className="p-4 md:p-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
              <div>
                <h3 className="text-sm font-bold text-white">{unpaidCount} users haven't subscribed yet</h3>
                <p className="text-xs text-muted-foreground mt-0.5">Send them a reminder email to complete their sign up</p>
              </div>
              <Button
                onClick={handleNudge}
                disabled={nudging}
                className="bg-blue-600 text-white hover:bg-blue-700 rounded-sm font-bold uppercase tracking-wider text-xs"
                data-testid="nudge-unpaid-button"
              >
                <Mail className="w-4 h-4 mr-2" />
                {nudging ? 'Sending...' : `Nudge ${unpaidCount} Users`}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Retention Email Triggers */}
        <Card className="bg-card border-border">
          <CardContent className="p-4 md:p-6">
            <div className="flex items-center gap-2 mb-3">
              <BarChart3 className="w-4 h-4 text-primary" />
              <h3 className="text-sm font-bold text-white uppercase tracking-wide">Retention Emails</h3>
            </div>
            <p className="text-xs text-muted-foreground mb-4">Auto-running in the background. Use these buttons to trigger a manual send right now.</p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={handleSendWeekly}
                disabled={sendingWeekly}
                variant="outline"
                className="rounded-sm font-bold uppercase tracking-wider text-xs"
                data-testid="send-weekly-progress-button"
              >
                <Send className="w-4 h-4 mr-2" />
                {sendingWeekly ? 'Sending...' : 'Send Weekly Progress (All Users)'}
              </Button>
              <Button
                onClick={handleSendWinback}
                disabled={sendingWinback}
                variant="outline"
                className="rounded-sm font-bold uppercase tracking-wider text-xs"
                data-testid="send-winback-button"
              >
                <Send className="w-4 h-4 mr-2" />
                {sendingWinback ? 'Sending...' : 'Send Win-Back (7+ Days Inactive)'}
              </Button>
              <Button
                onClick={handleSyncBurpees}
                disabled={syncingBurpees}
                variant="outline"
                className="rounded-sm font-bold uppercase tracking-wider text-xs"
                data-testid="sync-burpees-button"
              >
                {syncingBurpees ? 'Syncing...' : 'Sync Burpee Photos'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Users Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="bg-card border-border">
            <CardContent className="p-0">
              <div className="p-4 md:p-6 border-b border-border">
                <h2 className="text-lg font-bold uppercase tracking-wide">All Users ({users.length})</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="admin-users-table">
                  <thead>
                    <tr className="border-b border-border text-left text-xs text-muted-foreground uppercase tracking-wider">
                      <th className="p-3 cursor-pointer hover:text-white" onClick={() => handleSort('name')}>
                        Name <SortIcon field="name" />
                      </th>
                      <th className="p-3 cursor-pointer hover:text-white" onClick={() => handleSort('email')}>
                        Email <SortIcon field="email" />
                      </th>
                      <th className="p-3 cursor-pointer hover:text-white" onClick={() => handleSort('is_vip')}>
                        Status <SortIcon field="is_vip" />
                      </th>
                      <th className="p-3 cursor-pointer hover:text-white" onClick={() => handleSort('current_badge')}>
                        Level <SortIcon field="current_badge" />
                      </th>
                      <th className="p-3 cursor-pointer hover:text-white" onClick={() => handleSort('workouts_completed')}>
                        Workouts <SortIcon field="workouts_completed" />
                      </th>
                      <th className="p-3 cursor-pointer hover:text-white" onClick={() => handleSort('created_at')}>
                        Joined <SortIcon field="created_at" />
                      </th>
                      <th className="p-3">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayedUsers.map((u) => (
                      <tr key={u.user_id} className="border-b border-border/50 hover:bg-zinc-900/50 transition-colors" data-testid={`admin-user-row-${u.user_id}`}>
                        <td className="p-3 font-medium text-white">{u.name || 'N/A'}</td>
                        <td className="p-3 text-muted-foreground">{u.email}</td>
                        <td className="p-3">
                          {u.is_vip ? (
                            <span className="px-2 py-0.5 bg-green-500/10 text-green-400 border border-green-500/20 rounded-full text-xs font-bold">VIP</span>
                          ) : (
                            <span className="px-2 py-0.5 bg-zinc-800 text-zinc-400 border border-zinc-700 rounded-full text-xs font-bold">FREE</span>
                          )}
                        </td>
                        <td className="p-3 text-muted-foreground">{u.current_badge || 'Alpha Dad'}</td>
                        <td className="p-3 font-bold">{u.workouts_completed}</td>
                        <td className="p-3 text-muted-foreground">{formatDate(u.created_at)}</td>
                        <td className="p-3">
                          {u.email === 'jamesriedel88@gmail.com' ? (
                            <span className="text-xs text-muted-foreground">—</span>
                          ) : u.is_vip ? (
                            <button
                              onClick={() => setCancelTarget(u)}
                              className="inline-flex items-center gap-1 px-2 py-1 bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/20 rounded-sm text-xs font-bold uppercase tracking-wider transition-colors"
                              data-testid={`cancel-sub-button-${u.user_id}`}
                            >
                              <XCircle className="w-3 h-3" />
                              Cancel
                            </button>
                          ) : (
                            <button
                              onClick={() => handleGrantVIP(u)}
                              disabled={grantingId === u.user_id}
                              className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-500/10 text-yellow-400 hover:bg-yellow-500/20 border border-yellow-500/20 rounded-sm text-xs font-bold uppercase tracking-wider transition-colors disabled:opacity-50"
                              data-testid={`grant-vip-button-${u.user_id}`}
                            >
                              <Crown className="w-3 h-3" />
                              {grantingId === u.user_id ? 'Granting...' : 'Grant VIP'}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {users.length > 20 && (
                <div className="p-4 text-center border-t border-border">
                  <button
                    onClick={() => setShowAllUsers(!showAllUsers)}
                    className="text-primary text-sm font-bold uppercase tracking-wider hover:underline"
                    data-testid="show-all-users-button"
                  >
                    {showAllUsers ? 'Show Less' : `Show All ${users.length} Users`}
                  </button>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Recent Transactions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card className="bg-card border-border">
            <CardContent className="p-0">
              <div className="p-4 md:p-6 border-b border-border">
                <h2 className="text-lg font-bold uppercase tracking-wide">Recent Transactions</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="admin-transactions-table">
                  <thead>
                    <tr className="border-b border-border text-left text-xs text-muted-foreground uppercase tracking-wider">
                      <th className="p-3">Email</th>
                      <th className="p-3">Amount</th>
                      <th className="p-3">Status</th>
                      <th className="p-3">Type</th>
                      <th className="p-3">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="p-6 text-center text-muted-foreground">No transactions yet</td>
                      </tr>
                    ) : (
                      transactions.slice(0, 20).map((tx) => (
                        <tr key={tx.transaction_id} className="border-b border-border/50 hover:bg-zinc-900/50 transition-colors">
                          <td className="p-3 text-muted-foreground">{tx.email}</td>
                          <td className="p-3 font-bold text-green-400">${tx.amount} {(tx.currency || 'aud').toUpperCase()}</td>
                          <td className="p-3">
                            {tx.payment_status === 'paid' ? (
                              <span className="px-2 py-0.5 bg-green-500/10 text-green-400 border border-green-500/20 rounded-full text-xs font-bold">PAID</span>
                            ) : (
                              <span className="px-2 py-0.5 bg-yellow-500/10 text-yellow-400 border border-yellow-500/20 rounded-full text-xs font-bold">{(tx.payment_status || 'pending').toUpperCase()}</span>
                            )}
                          </td>
                          <td className="p-3 text-muted-foreground">{tx.type || tx.plan || 'one-time'}</td>
                          <td className="p-3 text-muted-foreground">{formatDate(tx.created_at)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Cancel Subscription Confirmation Dialog */}
      <Dialog open={!!cancelTarget} onOpenChange={(open) => !open && setCancelTarget(null)}>
        <DialogContent data-testid="cancel-sub-dialog">
          <DialogHeader>
            <DialogTitle>Cancel Subscription?</DialogTitle>
            <DialogDescription>
              This will immediately cancel the Stripe subscription for{' '}
              <span className="font-bold text-white">{cancelTarget?.name || cancelTarget?.email}</span>{' '}
              and revoke their VIP access. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setCancelTarget(null)}
              disabled={cancelling}
              data-testid="cancel-sub-dialog-cancel"
            >
              Keep Subscription
            </Button>
            <Button
              onClick={handleCancelUser}
              disabled={cancelling}
              className="bg-red-600 text-white hover:bg-red-700"
              data-testid="cancel-sub-dialog-confirm"
            >
              {cancelling ? 'Cancelling...' : 'Yes, Cancel'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
